/**
 * Identity Provider client hook.
 *
 * Today: thin wrapper around `@auth0/auth0-react`'s `useAuth0()`.
 * Migration: replace the internals with the Zitadel SDK or a generic OIDC
 * library. Consumers (`useLogout`, `usePostLoginOrchestration`, `Login`,
 * `Callback`, `SignUp`) call this hook only — never `useAuth0` directly.
 *
 * Naming: provider-agnostic. Method names match standard OIDC concepts.
 *
 * @module client/Auth/useIdpClient
 */

import {useCallback, useEffect, useState} from 'react';
import {useAuth0, type IdToken} from '@auth0/auth0-react';
import IDP_CONFIG from './idp-config';
import {tabSession} from './session';
import type {TabCredentials} from './tabSession';
import type {FirmChoice} from './zitadelClient';
import {callbackFirms, getZitadelClient, navigate, startupCallback} from './zitadelSession';
import type {RefreshedAccessToken} from '../lib/picos/socketAuthentication';

export interface IdpClient {
    /** True once the SDK has finished its initial state-determination phase. */
    isLoading: boolean;
    /** True if a valid session exists in this tab. */
    isAuthenticated: boolean;
    /** Latest error from the SDK, or undefined. */
    error: Error | undefined;

    /** Trigger an interactive login redirect. */
    loginWithRedirect: (opts?: LoginRedirectOptions) => Promise<void>;
    /** Reauthenticate without unloading this tab's unfinished editors. */
    loginWithPopup: () => Promise<void>;

    /** Log out and (optionally) end the session at the IdP. */
    logout: (opts?: LogoutOptions) => Promise<void> | void;

    /** Resolve an access token using the provider's normal cache policy. */
    getAccessTokenSilently: () => Promise<string>;

    /** Renew the credentials bound to this tab and preserve their account identity. */
    refreshAccessToken: () => Promise<AccessTokenRefresh>;

    /** Get the parsed ID-token claims, including custom claims. */
    getIdTokenClaims: () => Promise<IdpIdTokenClaims | undefined>;

    /** Zitadel only: the firms to choose from when the sign-in named more than one. */
    firmChoices?: FirmChoice[];
    /** Zitadel only: authorize again, silently, with the chosen firm's roles. */
    chooseFirm?: (firmOrgId: string) => Promise<void>;
}

export type AccessTokenRefresh = RefreshedAccessToken;

export interface LoginRedirectOptions {
    /** Hint the IdP to pre-fill the login email (signup → login flow). */
    loginHint?: string;
    /** Where to navigate after the post-callback redirect. */
    returnTo?: string;
}

export interface LogoutOptions {
    /** URL to redirect to after the IdP session ends. */
    returnTo?: string;
    /** Skip the IdP-side session end (clear local session only). */
    localOnly?: boolean;
}

/**
 * ID-token claims surfaced to consumers. The custom-claim namespace is mapped
 * by `usePostLoginOrchestration` (see `IDP_CONFIG`-resolved namespaces — same
 * keys the server uses via `config.jwt.appMetadataClaim`).
 */
export interface IdpIdTokenClaims {
    sub?: string;
    email?: string;
    exp?: number;
    /** Raw JWT from the SDK's tab-local cache. */
    __raw?: string;

    [claim: string]: unknown;
}

/**
 * Hook returning the active IdP client: Auth0 or Zitadel, fixed for the page by `IDP_CONFIG`,
 * so the hook called is the same on every render. Consumers do not change.
 */
export const useIdpClient: () => IdpClient =
    IDP_CONFIG.provider === 'zitadel' ? useZitadelIdpClient : useAuth0IdpClient;

function decodeClaims(token: string): IdpIdTokenClaims {
    const payload = token.split('.')[1] ?? '';
    return {...JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))), __raw: token};
}

/** The pair a startup callback returned, held until the post-login sequence binds it. */
let callbackCredentials: TabCredentials | undefined;

function useZitadelIdpClient(): IdpClient {
    const callback = startupCallback();
    const [state, setState] = useState<{isLoading: boolean; isAuthenticated: boolean; error?: Error;
        firmChoices?: FirmChoice[]}>(() => ({isLoading: !!callback && !callbackCredentials,
        isAuthenticated: !!callbackCredentials || tabSession.isLocalReady()}));

    useEffect(() => {
        if (!callback || callbackCredentials) return;
        let live = true;
        callback.then(result => {
            if (!live) return;
            const firms = callbackFirms(result);
            // Several firms: the person chooses, and the tab authorizes again with that firm's roles.
            if (firms.length > 1) { setState({isLoading: false, isAuthenticated: false, firmChoices: firms}); return; }
            callbackCredentials = result.credentials;
            setState({isLoading: false, isAuthenticated: true});
        }, (error: unknown) => {
            if (live) setState({isLoading: false, isAuthenticated: false,
                error: error instanceof Error ? error : new Error('Sign-in failed.')});
        });
        return () => { live = false; };
    }, [callback]);

    const refreshAccessToken = useCallback(async (): Promise<AccessTokenRefresh> => {
        const operation = tabSession.captureRequest();
        const credentials = await tabSession.credentials(operation, true);
        operation.assertCurrent();
        return {token: credentials.accessToken, idToken: credentials.idToken, expiresAt: tabSession.getNetworkExpiry()!};
    }, []);

    const loginWithRedirect = useCallback(async (opts?: LoginRedirectOptions) => {
        navigate(await getZitadelClient().authorizationUrl({prompt: 'login',
            loginHint: opts?.loginHint, returnTo: opts?.returnTo}));
    }, []);

    const chooseFirm = useCallback(async (firmOrgId: string) => {
        navigate(await getZitadelClient().authorizationUrl({firmOrgId, prompt: 'none'}));
    }, []);

    const loginWithPopup = useCallback(async () => {
        // A redirect would unload this tab's unfinished editors; sign in again from the login page.
        throw new Error('Sign in again from the login page to reopen this workspace.');
    }, []);

    // Local sign-out only, which is all the app asks for today; ending the Zitadel session is not wired.
    const logout = useCallback(async (_opts?: LogoutOptions) => {
        getZitadelClient().clear();
        callbackCredentials = undefined;
    }, []);

    const getAccessTokenSilently = useCallback(async () =>
        callbackCredentials?.accessToken ?? (await tabSession.credentials()).accessToken, []);

    // The post-login sequence reads the access token, then the claims; the claims read hands the
    // callback pair over, after which the tab session is the only source.
    const getIdTokenClaims = useCallback(async () => {
        const token = callbackCredentials?.idToken ?? tabSession.peekCredentials()?.idToken;
        callbackCredentials = undefined;
        return token ? decodeClaims(token) : undefined;
    }, []);

    return {
        isLoading: state.isLoading,
        isAuthenticated: state.isAuthenticated || (!callback && tabSession.isLocalReady()),
        error: state.error,
        loginWithRedirect,
        loginWithPopup,
        logout,
        getAccessTokenSilently,
        refreshAccessToken,
        getIdTokenClaims,
        firmChoices: state.firmChoices,
        chooseFirm,
    };
}

function useAuth0IdpClient(): IdpClient {
    const {
        isLoading,
        isAuthenticated,
        error,
        loginWithRedirect,
        getAccessTokenWithPopup,
        logout,
        getAccessTokenSilently,
        getIdTokenClaims,
    } = useAuth0();

    const refreshAccessToken = useCallback(async (): Promise<AccessTokenRefresh> => {
        const operation = tabSession.captureRequest();
        const credentials = await tabSession.credentials(operation, true);
        operation.assertCurrent();
        return {token: credentials.accessToken, idToken: credentials.idToken, expiresAt: tabSession.getNetworkExpiry()!};
    }, []);

    const interactiveLogin = useCallback(async (opts?: LoginRedirectOptions) => {
        await loginWithRedirect({
            authorizationParams: {prompt: 'login', ...(opts?.loginHint ? {login_hint: opts.loginHint} : {})},
            appState: opts?.returnTo ? {returnTo: opts.returnTo} : undefined,
        });
    }, [loginWithRedirect]);

    const reauthenticate = useCallback(async () => {
        // Auth0 React's loginWithPopup catches popup errors and resolves. This
        // method rejects them, so cancellation cannot reopen a locked tab using
        // the old still-cached credential pair.
        const token = await getAccessTokenWithPopup({authorizationParams: {prompt: 'login'}});
        if (!token) throw new Error('Sign-in did not return an access token.');
    }, [getAccessTokenWithPopup]);

    const logoutSession = useCallback((opts?: LogoutOptions) => logout({
        openUrl: opts?.localOnly === false ? undefined : false,
        ...(opts?.returnTo ? {logoutParams: {returnTo: opts.returnTo}} : {}),
    }), [logout]);

    const readClaims = useCallback(async () => await getIdTokenClaims() as IdpIdTokenClaims | undefined,
        [getIdTokenClaims]);

    return {
        isLoading,
        isAuthenticated,
        error: error as Error | undefined,

        loginWithRedirect: interactiveLogin,
        loginWithPopup: reauthenticate,
        logout: logoutSession,

        getAccessTokenSilently,
        refreshAccessToken,

        getIdTokenClaims: readClaims,
    };
}

// Re-export the underlying IdToken type for callers that need raw claim access
// (e.g., the post-login orchestration that reads custom-namespaced claims).
export type {IdToken};
